import { expectedOf, gaugeBands, type GaugeBand } from "../expected";
import { bandOf, barNames, levelEnds } from "../glance";
import { Frame, key, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";

const FILL = { ok: "bg-green-600", warning: "bg-amber-500", critical: "bg-red-600" } as const;

/**
 * level: 1 to 6 horizontal bars, each filled to its value between min and max in its band's colour, with the name
 * and the value above it (VisionPlatform widget-library-2 WLB-10). A bar of an offline device keeps its value, greyed.
 */
export function Level({ widget, input }: WidgetProps) {
  const fmt = useWidgetText();
  const cfg = widget.config ?? {};
  const names = barNames(widget, input);
  const bars = (widget.bindings ?? [])
    .filter((b) => b.datapoint)
    .map((b, i) => {
      const point = input.points?.[key(b.device, b.datapoint ?? "")];
      const v = point?.latest?.value;
      const [min, max] = levelEnds(cfg, point);
      const bands = Array.isArray(cfg.bands) ? (cfg.bands as GaugeBand[]) : gaugeBands(expectedOf(point), min, max);
      const has = !!point && typeof v === "number";
      return {
        id: key(b.device, b.datapoint ?? ""),
        name: names[i] ?? "",
        text: has ? fmt.value(v, point) : fmt.t("state.noData"),
        frac: has ? Math.min(1, Math.max(0, (v - min) / (max - min || 1))) : 0,
        level: has ? bandOf(v, bands) : undefined,
        has,
        offline: input.devices?.[b.device]?.state === "offline",
      };
    });
  const any = bars.some((b) => b.has);
  const state = !any ? "empty" : bars.some((b) => b.offline) ? "offline" : "normal";
  const label = (widget.title ? `${widget.title}: ` : "") + bars.map((b) => `${b.name} ${b.text}`).join(", ");
  return (
    <Frame widget={widget} state={state} title={widget.title} label={label}>
      <ul className="flex flex-1 flex-col justify-center gap-2">
        {bars.map((b) => (
          <li key={b.id} className="min-w-0" {...(b.level ? { "data-band": b.level } : {})}>
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="truncate text-gray-700">{b.name}</span>
              <span className="flex shrink-0 items-baseline gap-1.5">
                {b.offline && <span className="text-xs text-amber-900">{fmt.t("state.offline")}</span>}
                <span className={`font-semibold tabular-nums ${b.offline ? "text-gray-500" : "text-gray-900"}`}>
                  {b.text}
                </span>
              </span>
            </div>
            <div aria-hidden="true" className="mt-1 h-2.5 overflow-hidden rounded-full bg-gray-100">
              {b.frac > 0 && (
                <div
                  className={`h-full rounded-full ${b.offline ? "bg-gray-400" : b.level ? FILL[b.level] : "bg-brand"}`}
                  style={{ width: `${(b.frac * 100).toFixed(1)}%` }}
                />
              )}
            </div>
          </li>
        ))}
      </ul>
    </Frame>
  );
}
